'use client';

import React from 'react';
import Image from 'next/image';
import { motion, type Variants } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Text, Heading } from '@/components/ui/design-system';

interface LegalHeroProps {
  title: string;
  description?: string;
  imageSrc?: string;
  imageAlt?: string;
  lastUpdated?: string;
  className?: string;
}

/**
 * LegalHero Component
 * 
 * A special hero component for legal pages that maintains consistency with
 * main page heroes but with a more minimal approach suitable for legal content.
 */
export function LegalHero({
  title,
  description,
  imageSrc,
  imageAlt = "Legal page banner",
  lastUpdated,
  className,
}: LegalHeroProps) {
  // Animation variants
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <section 
      className={cn(
        "relative overflow-hidden py-24 md:py-36 lg:py-40 w-full",
        className
      )}
    >
      {/* Background with banner image */}
      <div className="absolute inset-0 w-full h-full -z-10">
        {imageSrc ? (
          <div className="relative w-full h-full">
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              priority
              className="object-cover"
              sizes="100vw"
              quality={90}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/50 dark:from-gray-900/95 dark:via-gray-900/80 dark:to-gray-900/50" />
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-sky-200 via-blue-100 to-indigo-200 dark:bg-gradient-to-br dark:from-slate-900 dark:via-neutral-800 dark:to-slate-950">
            {/* Abstract Shapes */}
            <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-sky-300/20 dark:bg-sky-700/10 rounded-full blur-3xl animate-pulse-slow -z-10"></div>
            <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 bg-blue-200/30 dark:bg-blue-600/20 rounded-full blur-2xl animate-pulse-slower -z-10"></div>
            <div className="absolute top-1/3 right-1/5 w-1/4 h-1/4 bg-indigo-300/10 dark:bg-indigo-500/5 rounded-full blur-3xl animate-pulse-slowest -z-10"></div>
          </div>
        )}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Left-aligned content similar to Industries page */}
        <motion.div
          className="max-w-7xl text-left"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <Heading
              level={1}
              className="text-4xl sm:text-5xl md:text-6xl tracking-tight !leading-tight mb-6 break-words hyphens-auto"
              color="primary"
            >
              {title}
            </Heading>
          </motion.div>
          
          {description && (
            <motion.div variants={itemVariants}>
              <Text 
                variant="body-large"
                color="secondary"
                className="mt-6 text-lg md:text-xl max-w-4xl"
              >
                {description}
              </Text>
            </motion.div>
          )}
          
          <motion.div variants={itemVariants} className="mt-10">
            {lastUpdated && (
              <Text 
                variant="caption"
                color="muted"
              >
                Last updated: {lastUpdated}
              </Text>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
} 